import React from 'react';
import { arrayOf, bool, number, shape, string } from 'prop-types';
import { Link, useParams } from 'react-router-dom';

import clsx from 'clsx';
import { ChevronRightRounded } from '@mui/icons-material';

import { getSelectionURI } from 'utils/helpers/url';

import { useUserRolePermissions } from 'hooks';

// Due to peformance concerns, this component is intentionally not using any MUI components,
// making no function calls (including R), and not utilizing any styling generated by functions.
const Selector = props => {
  const { viewBy } = useParams();

  // permissions related to this selector are defined in CASREF-1420
  // https://issues.mmplp.net:8443/browse/CASREF-1420
  const { isSupplier, isInventoryOwner } = useUserRolePermissions();

  const { data, index, style, selectedIndex } = props;

  const { selectorsData, handleSelectorClick } = data;
  const {
    activatedLocations,
    baseProductsAvailable,
    baseProductsActive,
    customProductsAvailable,
    customProductsActive,
    id,
    locationsAvailable,
    nameShort,
    proprietaryProduct,
    supplierDirectedBlendsAvailable,
    supplierDirectedBlendsActive,
  } = selectorsData[index];
  const {
    baseProductLocations = 0,
    customProductLocations = 0,
    supplierDirectedBlendLocations = 0,
  } = activatedLocations ?? {};

  const isSelected = index === selectedIndex;

  const cardStyles = clsx({ 'selector-card': true, 'selector-card-selected': isSelected });
  const chipStyles = clsx({
    'selector-card-chip': true,
    'selector-card-chip-selected': isSelected,
  });
  const badgeStyles = clsx({
    'selector-card-badge': true,
    'selector-card-badge-general': !proprietaryProduct,
    'selector-card-badge-proprietary': proprietaryProduct,
  });

  const isBaseProduct = proprietaryProduct === null;

  const renderChip = (active, available) => (
    <span className={chipStyles}>
      {active} of {available}
    </span>
  );

  const renderLabels = () =>
    viewBy === 'product' ? (
      <>
        {isBaseProduct && isInventoryOwner && (
          <>
            {renderChip(baseProductLocations, locationsAvailable)}
            <span className="selector-card-label">Locations</span>
          </>
        )}
        {!isBaseProduct && (
          <>
            {isInventoryOwner && (
              <>
                {renderChip(customProductLocations, locationsAvailable)}
                <span className="selector-card-label">CP Locations</span>
              </>
            )}
            {isSupplier && (
              <>
                {renderChip(supplierDirectedBlendLocations, locationsAvailable)}
                <span className="selector-card-label">SDB Locations</span>
              </>
            )}
            <div className={badgeStyles}>{proprietaryProduct ? 'PP' : 'GP'}</div>
          </>
        )}
      </>
    ) : (
      <>
        {isInventoryOwner && (
          <>
            {renderChip(baseProductsActive, baseProductsAvailable)}
            <span className="selector-card-label">Base Products</span>
            {renderChip(customProductsActive, customProductsAvailable)}
            <span className="selector-card-label">Custom Products</span>
          </>
        )}
        {isSupplier && (
          <>
            {renderChip(supplierDirectedBlendsActive, supplierDirectedBlendsAvailable)}
            <span className="selector-card-label">Supplier Directed Blends</span>
          </>
        )}
      </>
    );

  return (
    // react-window requires a wrapper with a style prop to absolutely position list items
    // https://github.com/bvaughn/react-window#why-is-my-list-blank-when-i-scroll
    <div style={style}>
      <Link
        className="selector-link"
        onClick={() => handleSelectorClick(index)}
        to={`../self-authorizations/${viewBy}/${getSelectionURI(selectorsData[index])}`}
      />
      <article className={cardStyles} key={id} role="presentation">
        {/* role="presentation" allows us to use onClick on an article element. */}
        <h2 className="selector-card-title">{nameShort}</h2>
        <ChevronRightRounded className="selector-card-chevron" />
        {renderLabels()}
      </article>
    </div>
  );
};

export default Selector;

Selector.propTypes = {
  data: shape({
    selectorsData: arrayOf(
      shape({
        activatedLocations: shape({}),
        id: number,
        locationsAvailable: number,
        nameShort: string,
        proprietaryProduct: bool,
      })
    ),
  }).isRequired,
  index: number.isRequired,
  selectedIndex: number,
  style: shape({}).isRequired,
};

Selector.defaultProps = {
  selectedIndex: undefined,
};
